import React, { useState } from "react";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import ReCAPTCHA from "react-google-recaptcha";
import { updateAuth } from "../store/Reducers/authReducer";
import { updateItem } from "../store/Reducers/itemReducer";
import { updateHome } from "../store/Reducers/homeReducer";
import { useReduxStore } from "../hooks/useReduxStore";
import * as PaymentService from "../services/paymentService";
import subscriptionService from "../services/subscriptionService";
import { API } from "../services/API";

const cardElementOptions = {
  style: {
    base: {
      color: "#ffffff",
      fontFamily: '"Outfit", "Inter", sans-serif',
      fontSize: "15px",
      lineHeight: "22px",
      "::placeholder": {
        color: "#71717a",
      },
      iconColor: "#FF8C21",
    },
    invalid: {
      color: "#ef4444",
      iconColor: "#ef4444",
    },
  },
};

const CheckoutForm = ({
  amount = 10,
  id,
  handelModalType,
  isSubscription = false,
  packageId,
  packageData,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { dispatch, getState } = useReduxStore();
  const { userData } = getState("AUTH");
  const { email, name } = userData || {};

  const [cardState, setCardState] = useState({
    cardNumber: { complete: false, empty: true, error: null },
    cardExpiry: { complete: false, empty: true, error: null },
    cardCvc: { complete: false, empty: true, error: null },
  });

  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [isOtpVerified, setIsOtpVerified] = useState(false);

  // OTP Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpMessage, setOtpMessage] = useState("");
  const [otpError, setOtpError] = useState(null);
  const [resendVisible1, setResendVisible1] = useState(false);

  const handleFieldChange = (field, event) => {
    setCardState((prev) => ({
      ...prev,
      [field]: {
        complete: !!event.complete,
        empty: !!event.empty,
        error: event.error ? event.error.message : null,
      },
    }));
    if (paymentError) setPaymentError(null);
  };

  const handleRecaptcha = (token) => {
    setRecaptchaToken(token);
    setRecaptchaError(null);
  };

  const activeCardError =
    cardState.cardNumber.error ||
    cardState.cardExpiry.error ||
    cardState.cardCvc.error;

  const isCardComplete =
    cardState.cardNumber.complete &&
    cardState.cardExpiry.complete &&
    cardState.cardCvc.complete;

  const isFormValid = () => {
    return isCardComplete && !activeCardError
      && !!recaptchaToken;
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();

    if (!recaptchaToken) {
      setRecaptchaError("Please complete the CAPTCHA");
      return;
    }

    if (!isCardComplete) {
      setPaymentError("Please fill in complete card details");
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    try {
      handelModalType("loading");
      dispatch(updateAuth({ loading: true }));
      setPaymentError(null);

      const { ok, data } = await PaymentService.createIntent({
        amount,
        name,
        email,
        recaptchaToken,
      });
      const { intent, amount: intentAmount } = data || {};

      if (ok && intent) {
        const cardNumberElement = elements.getElement(CardNumberElement);
        const { error } = await stripe.confirmCardPayment(intent, {
          payment_method: {
            card: cardNumberElement,
            billing_details: {
              name: name || undefined,
              email: email || undefined,
            },
          },
        });

        if (error) {
          handelModalType("failed");
          setPaymentError(error.message || "Payment confirmation failed");
        } else {
          if (isSubscription) {
            // Subscription Payment Success Flow
            // Route: POST /subscribe, Body: { package_id, payment_intent }
            const finalPackageId = packageId || id || packageData?.id;
            const { ok: subOk, data: subData } = await subscriptionService.subscribe({
              package_id: finalPackageId,
              payment_intent: intent,
            });

            if (subOk) {
              const subscribedPackageData =
                subData?.data?.package || packageData || { id: finalPackageId };
              const userPackageEntry = {
                id: subData?.data?.user_package_id || 1,
                user_id: userData?.id,
                package_id: finalPackageId,
                expires_at: subData?.data?.expires_at || null,
                cancelled_at: null,
                package: subscribedPackageData,
              };

              const existingPackages = Array.isArray(userData?.active_user_packages)
                ? userData.active_user_packages.filter(
                  (p) => Number(p?.package_id) !== Number(finalPackageId)
                )
                : [];

              dispatch(
                updateAuth({
                  userData: {
                    ...userData,
                    is_subscribed: true,
                    package_id: finalPackageId,
                    package: subscribedPackageData,
                    subscription: subscribedPackageData,
                    active_user_packages: [userPackageEntry, ...existingPackages],
                  },
                })
              );
              dispatch(updateHome({ selectedPackage: null }));
              handelModalType("success");
            } else {
              handelModalType("failed");
              setPaymentError(
                subData?.message || "Failed to activate subscription. Please contact support."
              );
            }
          } else {
            const { ok: successOk } = await PaymentService.paymentSuccess({
              product_id: id,
              payment_intent: intent,
              intent_amount: intentAmount,
            });
            if (successOk) {
              dispatch(
                updateItem({
                  itemDetail: {
                    ...(packageData || {}),
                    is_purchased: true,
                  },
                })
              );
              handelModalType("success");
            } else {
              handelModalType("failed");
            }
          }
        }
      } else {
        handelModalType("failed");
        setPaymentError("Unable to initiate payment. Please try again.");
      }
    } catch (error) {
      handelModalType("failed");
      setPaymentError(error?.message || "An unexpected error occurred during payment.");
    } finally {
      dispatch(updateAuth({ loading: false }));
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setOtpError(null);
      const response = await fetch(
        `${API}payment/otp/verify`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData?.token}`,
          },
          body: JSON.stringify({ otp }),
        }
      );
      console.log("check response -----", response)
      if (!response?.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to verify OTP");
      }

      const result = await response?.json();
      console.log("check result -----", response)

      if (!result.ok) {
        setIsModalOpen(false);
        setIsOtpVerified(true);
        setOtpError(null);
      } else {
        throw new Error(
          result.message || "Unknown error during OTP verification"
        );
      }
    } catch (error) {
      setOtpError(error.message);
    }
  };

  const handleResendOtp = async () => {
    try {
      setOtpError(null);
      const response = await fetch(
        `${API}payment/otp/resend`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData?.token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to resend OTP");
      }
      setResendVisible1(true);
      setOtpMessage(
        "An OTP has been resent to your email. It will expire within 5 minutes."
      );
      setTimeout(() => {
        setResendVisible1(false);
      }, 120000);
    } catch (error) {
      setOtpError(error.message);
    }
  };

  const handleContinue = async () => {
    if (!isFormValid()) return;

    try {
      setPaymentError(null);
      setOtpError(null);
      const response = await fetch(
        `${API}payment/otp/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData?.token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send OTP. Please try again.");
      }

      setIsModalOpen(true);
    } catch (error) {
      setPaymentError(error.message);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Split Card Elements Layout */}
        <div className="space-y-3.5">
          {/* Card Number */}
          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1.5 uppercase tracking-wider">
              Card Number
            </label>
            <div
              className={`p-3 bg-[#111111] border rounded-xl transition-all ${cardState.cardNumber.error
                ? "border-red-500/80 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500"
                : "border-gray-800 focus-within:border-secondary focus-within:ring-1 focus-within:ring-secondary"
                }`}
            >
              <CardNumberElement
                options={{
                  ...cardElementOptions,
                  showIcon: true,
                  placeholder: "1234 1234 1234 1234",
                }}
                onChange={(e) => handleFieldChange("cardNumber", e)}
              />
            </div>
          </div>

          {/* Expiration Date & CVC in 2 Columns */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1.5 uppercase tracking-wider">
                Expiration Date
              </label>
              <div
                className={`p-3 bg-[#111111] border rounded-xl transition-all ${cardState.cardExpiry.error
                  ? "border-red-500/80 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500"
                  : "border-gray-800 focus-within:border-secondary focus-within:ring-1 focus-within:ring-secondary"
                  }`}
              >
                <CardExpiryElement
                  options={{
                    ...cardElementOptions,
                    placeholder: "MM / YY",
                  }}
                  onChange={(e) => handleFieldChange("cardExpiry", e)}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1.5 uppercase tracking-wider">
                CVC / CVV
              </label>
              <div
                className={`p-3 bg-[#111111] border rounded-xl transition-all ${cardState.cardCvc.error
                  ? "border-red-500/80 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500"
                  : "border-gray-800 focus-within:border-secondary focus-within:ring-1 focus-within:ring-secondary"
                  }`}
              >
                <CardCvcElement
                  options={{
                    ...cardElementOptions,
                    placeholder: "CVC",
                  }}
                  onChange={(e) => handleFieldChange("cardCvc", e)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Dynamic Error Messaging */}
        {(activeCardError || paymentError) && (
          <div className="text-xs text-red-500 font-medium bg-red-500/10 border border-red-500/20 py-2.5 px-3 rounded-lg flex items-center gap-2">
            <svg
              className="w-4 h-4 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span>{activeCardError || paymentError}</span>
          </div>
        )}

        {/* reCAPTCHA Error */}
        {recaptchaError && (
          <p className="text-xs text-red-500 font-medium">{recaptchaError}</p>
        )}

        {/* OTP Verified Success Banner */}
        {isOtpVerified && (
          <div className="text-xs text-green-500 font-medium bg-green-500/10 border border-green-500/20 py-2.5 px-3 rounded-lg flex items-center gap-2">
            <svg
              className="w-4 h-4 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span>OTP verified successfully! You can now proceed to pay.</span>
          </div>
        )}

        {/* Responsive reCAPTCHA */}
        <div className="flex justify-center md:justify-start overflow-hidden py-1">
          <div className="origin-center md:origin-left transform scale-[0.88] sm:scale-100 transition-transform">
            <ReCAPTCHA
              sitekey="6Le84TsqAAAAACaoQneF1ELV6M8LtgExgrcHGdPl"
              onChange={handleRecaptcha}
              theme="dark"
            />
          </div>
        </div>

        {/* Action Button */}
        {isOtpVerified ? (
          <button
            type="button"
            onClick={handleSubmit}
            className="block w-full bg-secondary hover:bg-orange-500 text-white rounded-xl px-4 py-3.5 font-semibold transition-all hover:scale-[1.01] active:scale-95 shadow-md shadow-secondary/20 cursor-pointer text-center tracking-wide"
          >
            PAY NOW
          </button>
        ) : (
          <button
            type="button"
            onClick={handleContinue}
            className={`block w-full transition-all rounded-xl px-4 py-3.5 font-semibold text-center tracking-wide ${isFormValid()
              ? "bg-secondary hover:bg-orange-500 hover:scale-[1.01] active:scale-95 shadow-md shadow-secondary/20 cursor-pointer text-white"
              : "bg-gray-800 text-gray-500 cursor-not-allowed"
              }`}
            disabled={!isFormValid()}
          >
            CONTINUE
          </button>
        )}

        {/* Security Trust Badge */}
        <div className="flex items-center justify-center gap-2 pt-1 text-gray-500 text-[11px]">
          <svg
            className="w-3.5 h-3.5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <span>Guaranteed safe & secure checkout powered by Stripe</span>
        </div>
      </form>

      {/* OTP Verification Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 w-full h-full bg-black/80 backdrop-blur-sm flex justify-center items-center z-[1000] p-4 transition-all">
          <div className="bg-[#141414] border border-gray-800 text-white p-6 md:p-8 rounded-2xl text-center relative max-w-md w-full shadow-2xl animate-fade-in">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors text-2xl font-bold"
              onClick={() => setIsModalOpen(false)}
            >
              &times;
            </button>
            <h3 className="font-bold text-xl md:text-2xl text-white mb-4">
              OTP Verification
            </h3>

            <div className="mb-4 mt-6 space-y-4">
              <div className="flex justify-center">
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 6) {
                      setOtp(value);
                    }
                  }}
                  placeholder="Enter 6-digit OTP"
                  className="bg-[#1e1e1e] border border-gray-850 text-white text-center rounded-xl px-4 py-3 w-3/4 text-lg font-bold tracking-widest focus:border-secondary focus:ring-1 focus:ring-secondary transition-all outline-none"
                  required
                  maxLength={6}
                />
              </div>

              <p className="text-xs text-gray-400">
                An OTP has been sent to your email. It will expire within 5 minutes.
              </p>

              {otpError && (
                <div>
                  <p className="text-xs text-red-500 font-medium bg-red-500/10 border border-red-500/20 py-2 px-3 rounded-lg">
                    {otpError}
                  </p>
                </div>
              )}

              {otpMessage && (
                <div>
                  <p className="text-xs text-green-500 font-medium bg-green-500/10 border border-green-500/20 py-2 px-3 rounded-lg">
                    {otpMessage}
                  </p>
                </div>
              )}

              <div className="pt-2">
                <button
                  disabled={resendVisible1}
                  type="button"
                  onClick={handleResendOtp}
                  className={`text-xs font-medium transition-all ${resendVisible1
                    ? "text-gray-600 cursor-not-allowed"
                    : "text-secondary hover:underline cursor-pointer"
                    }`}
                >
                  Resend OTP
                </button>
              </div>

              <button
                type="button"
                onClick={handleVerifyOtp}
                className="block mt-6 w-full bg-secondary hover:bg-orange-500 text-white rounded-xl px-4 py-3 font-semibold transition-all hover:scale-[1.01] active:scale-95 shadow-md shadow-secondary/15 cursor-pointer"
              >
                VERIFY OTP
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutForm;
